import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ChangeRequestRegisterResponse } from '@risksmart-app/trpc/src/types';
import type { GetChangeRequestsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC change request data to match the GraphQL query structure
 */
export const mapTrpcChangeRequestsToGraphQL = (
  trpcData: ChangeRequestRegisterResponse
): GetChangeRequestsQuery => {
  return {
    change_request: trpcData.change_request.map((item) => ({
      ...item,
      // Add missing GraphQL fields that TRPC doesn't provide
      currentUserOwnerList: item.parentOwnerAndContributors || [],
      parentOwners: item.parentOwnerAndContributors || [],
      // Transform parent structure to match GraphQL expectations
      parent: item.parent
        ? {
            ...item.parent,
            // Map nested parent entity relationships
            documentFile: item.document_file
              ? {
                  Version: item.document_file.Version,
                  parent: item.document_file.parent,
                }
              : null,
            acceptance: item.acceptance,
            risk: item.risk,
            control: item.control,
            action: item.action,
            issue_assessment: item.issue_assessment,
          }
        : null,
    })) as GetChangeRequestsQuery['change_request'],
  };
};

interface UseGetChangeRequestsRegisterTRPCParams {
  currentUserId: string;
  skip?: boolean;
}

export const useGetChangeRequestsRegisterTRPC = ({
  currentUserId,
  skip,
}: UseGetChangeRequestsRegisterTRPCParams) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.changeRequest.register.queryOptions(),
    enabled: trpcEnabled && !skip && !!currentUserId, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcChangeRequestsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
